import { useEffect, useMemo, useRef } from "react";
import { AudioTrack, MusicTrack, TimelineTime, TimelineTrack } from "../types";

export function AudioPlayer({
  tracks,
  activeTime,
  isPlaying,
}: {
  tracks: TimelineTrack[];
  activeTime: TimelineTime;
  isPlaying: boolean;
}) {
  const audioTracks = useMemo(
    () =>
      tracks.filter(
        (track): track is AudioTrack | MusicTrack =>
          track.kind === "audio" || track.kind === "music"
      ),
    [tracks]
  );

  const audioRefs = useRef<Record<string, HTMLAudioElement | null>>({});
  const pendingPlayRefs = useRef<Record<string, boolean>>({});
  const pendingLoadListenersRef = useRef<
    Record<string, (event: Event) => void>
  >({});

  // 1. Play/Pause and Sync Logic
  useEffect(() => {
    if (!audioTracks.length) return;

    audioTracks.forEach((track) => {
      track.items.forEach((audioItem) => {
        const audio = audioRefs.current[audioItem.id];
        if (!audio) return;

        const isWithinWindow =
          activeTime >= audioItem.startTime && activeTime < audioItem.endTime;

        if (isPlaying && isWithinWindow) {
          const relativeTime = activeTime - audioItem.startTime;
          const targetSeconds = relativeTime / 1000;

          const playAudio = () => {
            if (pendingPlayRefs.current[audioItem.id]) return;

            // Only seek if paused or drift is significant (>150ms)
            const drift = Math.abs(audio.currentTime - targetSeconds);
            if (audio.paused || drift > 0.15) {
              audio.currentTime = targetSeconds;
            }

            if (audio.paused) {
              pendingPlayRefs.current[audioItem.id] = true;
              audio
                .play()
                .catch((error) => {
                  if (error.name !== "AbortError") {
                    console.warn(
                      "Failed to play audio:",
                      audioItem.asset.url,
                      error
                    );
                  }
                })
                .finally(() => {
                  pendingPlayRefs.current[audioItem.id] = false;
                });
            }
          };

          if (audio.readyState >= 1) {
            playAudio();
          } else if (!pendingLoadListenersRef.current[audioItem.id]) {
            const onLoaded = () => {
              playAudio();
              audio.removeEventListener("loadedmetadata", onLoaded);
              delete pendingLoadListenersRef.current[audioItem.id];
            };
            pendingLoadListenersRef.current[audioItem.id] = onLoaded;
            audio.addEventListener("loadedmetadata", onLoaded);
          }
        } else {
          // Pause and cleanup if playing or has pending listener
          if (!audio.paused) {
            audio.pause();
          }
          const listener = pendingLoadListenersRef.current[audioItem.id];
          if (listener) {
            audio.removeEventListener("loadedmetadata", listener);
            delete pendingLoadListenersRef.current[audioItem.id];
          }
        }
      });
    });
  }, [isPlaying, activeTime, audioTracks]);

  // 2. Cleanup on unmount
  useEffect(() => {
    const currentAudioRefs = audioRefs.current;
    const currentListeners = pendingLoadListenersRef.current;
    return () => {
      Object.entries(currentListeners).forEach(([audioId, listener]) => {
        currentAudioRefs[audioId]?.removeEventListener(
          "loadedmetadata",
          listener
        );
      });
    };
  }, []);

  return (
    <div className="hidden">
      {audioTracks.map((track) =>
        track.items.map((audioItem) => (
          <audio
            key={audioItem.id}
            ref={(node) => {
              audioRefs.current[audioItem.id] = node;
            }}
            src={audioItem.asset.url}
            preload="auto"
            crossOrigin="anonymous"
          />
        ))
      )}
    </div>
  );
}
